import {useEffect, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router-dom";
import {SubmitHandler, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormDescription,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {Input} from "@/components/ui/input";
import {RadioGroup, RadioGroupItem} from "@/components/ui/radio-group.tsx";
import {Button} from "@/components/ui/button";
import {Calendar} from "@/components/ui/calendar.tsx";
import {useToast} from "@/components/ui/use-toast";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Separator} from "@/components/ui/separator.tsx";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import {cn} from "@/lib/utils.ts";
import {format} from "date-fns";
import {Loader2} from "lucide-react";
import {CalendarIcon} from "@radix-ui/react-icons";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getMentorById, updateMentorWithAvailability} from "@/services/mentorService.ts";
import {getEffectiveDeadlines} from "@/services/deadlineExtensionService.ts";
import {Mentor, UpdateMentorAvailabilityDto} from "@/types/Mentor.ts";
import {adjustTimezone, convertTo12Hour, getTimeWindows, timezoneResolver} from "@/utils/time-helper.ts";
import {isPreferenceSubmissionPeriod, isBeforeMentorSelectionDeadline} from "@/utils/time-helper.ts";
import {useTranslation} from "react-i18next";
import {activeLocale, LocalizedField} from "@/utils/translation-helper.ts";
import {useAuth} from "@/contexts/AuthContext.tsx";
import {UserRole} from "@/types/UserRole.ts";

const MentorProfileUpdateAvailability = () => {
    const {t} = useTranslation();
    const {t: vt} = useTranslation("validation");
    const {id} = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {toast} = useToast();
    const {loggedInUser} = useAuth();
    const [isMentorFetched, setIsMentorFetched] = useState(false);

    const {data: effectiveDeadlines} = useQuery({
        queryKey: queryKeys.effectiveDeadlines,
        queryFn: getEffectiveDeadlines,
        enabled: !!loggedInUser?.id,
    });
    const submitPreferenceDeadline = effectiveDeadlines?.preferenceSubmission
        ? new Date(effectiveDeadlines.preferenceSubmission)
        : undefined;
    const mentorSelectionDeadline = effectiveDeadlines?.mentorSelection
        ? new Date(effectiveDeadlines.mentorSelection)
        : undefined;
    const isMentor = loggedInUser?.role === UserRole.MENTOR;
    const isAdmin = loggedInUser?.role === UserRole.ADMIN;
    const mentorPastDeadline = isMentor && !isPreferenceSubmissionPeriod(submitPreferenceDeadline);
    const adminPastDeadline = isAdmin && !isBeforeMentorSelectionDeadline(mentorSelectionDeadline);
    const cannotEditAvailability = mentorPastDeadline || adminPastDeadline;

    const { data: mentor } = useQuery({
        queryKey: queryKeys.fetchMentors.one,
        queryFn: () => getMentorById(id ? parseInt(id) : -1),
    });

    const options = getTimeWindows();

    const availabilitySchema = z.object({
        availableOn: z
            .date({
                required_error: vt("selectOne", {fieldName: t("common.date")}),
            }),
        availableFrom: z
            .string({
                required_error: vt("selectOne", {fieldName: t("mentor.startTime")}),
            }),
        availableTill: z
            .string({
                required_error: vt("selectOne", {fieldName: t("mentor.endTime")}),
            }),
    });

    const availabilityFormSchema = z.object({
        timezone: z.string().trim().min(1, {
            message: vt("selectOne", {fieldName: t("common.timezone")}),
        }),
        isOnWhatsApp: z.boolean({
            required_error: vt("selectMentorshipStyle"),
        }),
        availabilities: z.array(availabilitySchema),
        personalMeetingLink: z.string().url({message: vt("urlWrongFormat")}),
        phoneNumber: z.string().regex(
            /^(\(\d{3}\)\s|\d{3}[-.\s])\d{3}[-.\s]\d{4}$/,
            {
                message: vt("phoneNumberWrongFormat"),
            }
        ),
    });

    type ProfileFormValues = z.infer<typeof availabilityFormSchema>;

    const form = useForm<ProfileFormValues>({
        resolver: zodResolver(availabilityFormSchema),
        mode: "onChange",
        defaultValues: {
            availabilities: [],
        }
    });

    const isOnWhatsApp = form.watch("isOnWhatsApp");
    const sessionsPerSeason = parseInt(import.meta.env.VITE_MENTOR_SESSIONS_PER_SEASON ?? "5", 10) || 5;
    const sessionsWithWhatsApp = parseInt(import.meta.env.VITE_MENTOR_SESSIONS_WITH_WHATSAPP ?? "3", 10) || 3;
    const slotCount = isOnWhatsApp ? sessionsWithWhatsApp : sessionsPerSeason;

    useEffect(() => {
        const current = form.getValues("availabilities");
        const empty = { availableOn: undefined, availableFrom: undefined, availableTill: undefined };
        const next = Array.from({ length: slotCount }, (_, i) => current[i] ?? empty);
        form.setValue("availabilities", next);
    }, [isOnWhatsApp, slotCount, form]);

    useEffect(() => {
        if (mentor) {
            const count = mentor.isOnWhatsApp ? sessionsWithWhatsApp : sessionsPerSeason;
            const mapped = (mentor.availabilities ?? []).map(a => ({
                availableOn: adjustTimezone(a.availableOn),
                availableFrom: convertTo12Hour(a.availableFrom),
                availableTill: convertTo12Hour(a.availableTill),
            }));
            const empty = { availableOn: undefined, availableFrom: undefined, availableTill: undefined };
            const availabilities = Array.from({ length: count }, (_, i) => mapped[i] ?? empty);
            form.reset({
                timezone: mentor.timezone ?? "",
                isOnWhatsApp: mentor.isOnWhatsApp,
                availabilities,
                personalMeetingLink: mentor.personalMeetingLink ?? "",
                phoneNumber: mentor.phoneNumber ?? "",
            });
            setIsMentorFetched(true);
        }
    }, [mentor, sessionsPerSeason, sessionsWithWhatsApp]);

    const onRequestError = () => {
        toast({
            variant: 'destructive',
            title: t("common.errorTitle"),
            description: t("common.errorDesc"),
        });
    };

    const onUpdateSuccess = async (mentor: Mentor) => {
        navigate(`/mentors/${mentor.id}`);
        await queryClient.setQueryData(queryKeys.fetchMentors.one, () => mentor);
        toast({
            description: t("profile.updateSuccess"),
        });
    };

    const updateMutation = useMutation({
        mutationFn: ({id, ...dto}: UpdateMentorAvailabilityDto & {
            id: number
        }) => updateMentorWithAvailability(id, dto),
        onSuccess: onUpdateSuccess,
        onError: onRequestError,
    });

    const onSubmit: SubmitHandler<ProfileFormValues> = (values) => {
        if (mentor) {
            const updateDto = {
                timezone: values.timezone,
                isOnWhatsApp: values.isOnWhatsApp,
                availabilities: values.availabilities.map(a => ({
                    availableOn: a.availableOn,
                    availableFrom: a.availableFrom,
                    availableTill: a.availableTill,
                })),
                personalMeetingLink: values.personalMeetingLink,
                phoneNumber: values.phoneNumber,
            };

            updateMutation.mutate({...updateDto, id: mentor.id});
        }
    };

    return (
        <Card className="border border-border bg-bg-card shadow-sm">
            <CardHeader className="border-b border-border">
                <CardTitle className="text-xl font-semibold text-text-1">{t("mentor.availability")}</CardTitle>
                <CardDescription className="text-text-2">
                    {t("mentor.availabilityDesc")}
                </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
                {cannotEditAvailability && (
                    <div className="rounded-lg border border-border bg-bg-elevated/50 p-4 text-sm text-text-2">
                        {adminPastDeadline ? t("mentor.availabilityPastDeadlineAdmin") : t("mentor.availabilityPastDeadline")}
                    </div>
                )}

                {!isMentorFetched && !cannotEditAvailability && (
                    <div className="flex flex-col space-y-3">
                        <div className="space-y-2">
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                            <Skeleton className="h-8 w-full"/>
                        </div>
                    </div>
                )}

                {isMentorFetched && !cannotEditAvailability && (
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                        <FormField
                            control={form.control}
                            name="timezone"
                            render={({field}) => (
                                <FormItem>
                                    <FormLabel className="text-lg">
                                        {t("common.stepXofY", {current: 1, total: 5})}: {t("mentor.selectTimezone")}
                                    </FormLabel>
                                    <Select onValueChange={field.onChange}
                                            value={field.value}
                                    >
                                        <FormControl>
                                            <SelectTrigger>
                                                <SelectValue placeholder={t("mentor.selectTimezone")}/>
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                            {Object.keys(timezoneResolver).map(timezone => (
                                                <SelectItem key={timezone} value={timezone}>
                                                    {timezoneResolver[timezone][activeLocale() as keyof LocalizedField]}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="isOnWhatsApp"
                            render={({field}) => (
                                <FormItem className="space-y-3 !mt-12">
                                    <FormLabel className="text-lg">
                                        {t("common.stepXofY", {current: 2, total: 5})}: {vt("selectMentorshipStyle")}
                                    </FormLabel>
                                    <FormControl>
                                        <RadioGroup
                                            onValueChange={value => field.onChange(value === 'true')}
                                            value={(field.value || field.value === false) ? field.value.toString() : undefined}
                                            className="flex flex-col space-y-1"
                                        >
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="false"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("mentor.nSessionsPerYear")}
                                                </FormLabel>
                                            </FormItem>
                                            <FormItem className="flex items-center space-x-3 space-y-0">
                                                <FormControl>
                                                    <RadioGroupItem value="true"/>
                                                </FormControl>
                                                <FormLabel className="font-normal">
                                                    {t("mentor.threeSessionsPerYear")}
                                                </FormLabel>
                                            </FormItem>
                                        </RadioGroup>
                                    </FormControl>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />

                        <h3 className="text-lg font-medium !mt-12">
                            {t("common.stepXofY", {current: 3, total: 5})}: {t("mentor.scheduleYourSessions")}
                        </h3>
                        <p className="text-sm text-text-2 !mt-2">
                            {t("mentor.scheduleYourSessionsDesc")}
                        </p>

                        {Array.from({ length: slotCount }).map((_, index) => (
                            <div key={index} className="space-y-6">
                                <h4 className="text-lg">{t("mentor.session")} {index + 1}</h4>
                                <Separator/>
                                <FormField
                                    control={form.control}
                                    name={`availabilities.${index}.availableOn`}
                                    render={({field}) => (
                                        <FormItem className="flex flex-col">
                                            <FormLabel>{t("common.date")}</FormLabel>
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <FormControl>
                                                        <Button
                                                            variant={"outline"}
                                                            className={cn(
                                                                "pl-3 text-left font-normal",
                                                                !field.value && "text-text-3"
                                                            )}
                                                        >
                                                            {field.value ? (
                                                                format(field.value, "PPP")
                                                            ) : (
                                                                <span>{t("common.pickDate")}</span>
                                                            )}
                                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50"/>
                                                        </Button>
                                                    </FormControl>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="start">
                                                    <Calendar
                                                        mode="single"
                                                        selected={field.value}
                                                        onSelect={field.onChange}
                                                        disabled={date => date < new Date()}
                                                        initialFocus
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name={`availabilities.${index}.availableFrom`}
                                    render={({field}) => (
                                        <FormItem>
                                            <FormLabel>{t("common.time")}</FormLabel>
                                            <Select
                                                onValueChange={(value) => {
                                                    form.setValue(`availabilities.${index}.availableTill`, options[value]["end"]);
                                                    field.onChange(value);
                                                }}
                                                value={field.value}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t("mentor.selectStartTime")}/>
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {Object.keys(options).slice(0, -1).map((time) => (
                                                        <SelectItem key={time} value={time}>
                                                            {options[time]["start"]} - {options[time]["end"]}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage/>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name={`availabilities.${index}.availableTill`}
                                    render={({field}) => (
                                        <Input type="hidden" {...field} />
                                    )}
                                />
                            </div>
                        ))}
                        <FormField
                            control={form.control}
                            name="personalMeetingLink"
                            render={({field}) => (
                                <FormItem className="!mt-12">
                                    <FormLabel className="text-lg">
                                        {t("common.stepXofY", {
                                            current: 4,
                                            total: 5
                                        })}: {t("mentor.personalMeetingLink")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("mentor.enterZoomLink")} {...field} />
                                    </FormControl>
                                    <FormDescription>
                                        {t("mentor.seeInstructionsTo")} <a className="text-arcticBlue" target="_blank"
                                                                           href="https://support.zoom.com/hc/en/article?id=zm_kb&sysparm_article=KB0066271">{t("mentor.findPersonalZoomLink")}</a>
                                    </FormDescription>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name="phoneNumber"
                            render={({field}) => (
                                <FormItem className="!mt-12">
                                    <FormLabel className="text-lg">
                                        {t("common.stepXofY", {
                                            current: 5,
                                            total: 5
                                        })}: {t("common.phoneNumber")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input placeholder={t("common.phoneNumberPlaceholder")} {...field} />
                                    </FormControl>
                                    <FormDescription>
                                        {t("common.phoneNumberFormat")}
                                    </FormDescription>
                                    <FormMessage/>
                                </FormItem>
                            )}
                        />
                        <Button
                            type="submit"
                            disabled={!form.formState.isDirty || updateMutation.isPending}
                        >
                            {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                            {t("mentor.updateAvailability")}
                        </Button>
                        </form>
                    </Form>
                )}
            </CardContent>
        </Card>
    );
};

export default MentorProfileUpdateAvailability;
